import { Entity } from './Entity';
import { Component } from './Component';
import { Logger } from '../../utils/Logger';

/**
 * Manages components for all entities in the ECS architecture
 */
export class ComponentManager {
  private static instance: ComponentManager;
  private components: Map<number, Map<Function, Component>>;
  private logger: Logger;

  private constructor() {
    this.components = new Map();
    this.logger = Logger.getInstance();
  }

  public static getInstance(): ComponentManager {
    if (!ComponentManager.instance) {
      ComponentManager.instance = new ComponentManager();
    }
    return ComponentManager.instance;
  }

  /**
   * Add a component to an entity
   */
  public addComponent(entity: Entity, component: Component): void {
    const entityId = entity.getId();
    if (!this.components.has(entityId)) {
      this.components.set(entityId, new Map());
    }
    const entityComponents = this.components.get(entityId)!;
    entityComponents.set(component.constructor, component);
    component.setEntity(entity);
  }

  /**
   * Remove a component from an entity
   */
  public removeComponent(entity: Entity, componentType: Function): void {
    const entityComponents = this.components.get(entity.getId());
    if (entityComponents) {
      entityComponents.delete(componentType);
    }
  }

  /**
   * Get a component from an entity
   */
  public getComponent<T extends Component>(
    entity: Entity,
    componentType: Function
  ): T | null {
    const entityComponents = this.components.get(entity.getId());
    if (entityComponents) {
      return (entityComponents.get(componentType) as T) || null;
    }
    return null;
  }

  /**
   * Check if an entity has a component
   */
  public hasComponent(entity: Entity, componentType: Function): boolean {
    const entityComponents = this.components.get(entity.getId());
    return entityComponents ? entityComponents.has(componentType) : false;
  }

  /**
   * Get all entities that have a specific component
   */
  public getEntitiesWithComponent(componentType: Function): Entity[] {
    const entities: Entity[] = [];
    for (const [entityId, components] of Array.from(
      this.components.entries()
    )) {
      if (components.has(componentType)) {
        const component = components.get(componentType);
        if (component && component.getEntity()) {
          entities.push(component.getEntity()!);
        }
      }
    }
    return entities;
  }

  /**
   * Remove all components from an entity
   */
  public removeAllComponents(entity: Entity): void {
    this.components.delete(entity.getId());
  }

  /**
   * Clear all components from all entities
   */
  public clear(): void {
    this.components.clear();
  }
}
